import * as Sentry from '@sentry/vue';
import { AssistantService } from '@/api/services/assistant.service';
import { getServerErrorStatus } from '@/domain/reservations/reservation-errors';
import type { IAssistantIdentityTokenResponse } from '@/interfaces/assistant.interface';
import { readAssistantError } from './assistant/assistant-errors';
import { buildScopeKey } from './tools/tool-scope';

/** Who is chatting, where, and in which language: a token is only reused for the same. */
export interface IAssistantIdentityInput {
  userId: number;
  restaurantId: number;
  branchId: number;
  locale: string;
}

/** A token is renewed when it has less than this left (the widget's own margin). */
export const IDENTITY_EXPIRY_MARGIN_MS = 60_000;
/** After a refusal (not after a network error), the chat stays anonymous this long before asking again. */
export const IDENTITY_RETRY_AFTER_MS = 60_000;

interface ICachedToken {
  key: string;
  token: string;
  expiresAtMs: number;
}

let cached: ICachedToken | null = null;
/** Refused keys and when they may be asked again. */
const failedUntil = new Map<string, number>();
const inflight = new Map<string, Promise<string | null>>();
/** Grows on every reset, so a request started before it is not cached. */
let generation = 0;

/** `userId|scopeKey|locale` (plan §9.5). */
export function identityCacheKey(input: IAssistantIdentityInput): string {
  const scopeKey = buildScopeKey(input);
  return `${input.userId}|${scopeKey}|${input.locale}`;
}

/**
 * The API answered and would answer the same right away (the identity is
 * not configured, the member may not have it, the route does not exist):
 * asking again soon only repeats it. Network errors, timeouts and other
 * server errors are not refusals.
 */
function isRefusal(error: unknown): boolean {
  const status = getServerErrorStatus(error);
  if (status !== null && status >= 400 && status < 500) return true;
  return readAssistantError(error)?.code === 'ASSISTANT_IDENTITY_OFF';
}

async function requestToken(
  key: string,
  input: IAssistantIdentityInput
): Promise<string | null> {
  const startedIn = generation;
  const fail = (error: unknown, refusal: boolean): null => {
    // Only a refusal keeps the chat anonymous for a while; after a network
    // blip the next call asks again, so the conversation keeps its owner.
    if (refusal && startedIn === generation)
      failedUntil.set(key, Date.now() + IDENTITY_RETRY_AFTER_MS);
    Sentry.captureMessage('Assistant identity token failed', {
      level: 'warning',
      tags: { feature: 'assistant' },
      extra: {
        restaurantId: input.restaurantId,
        branchId: input.branchId,
        refusal,
        error: error instanceof Error ? error.message : String(error),
      },
    });
    return null;
  };

  let response: IAssistantIdentityTokenResponse;
  try {
    response = await AssistantService.identityToken(input.restaurantId, {
      branchId: input.branchId,
      locale: input.locale,
    });
  } catch (error) {
    return fail(error, isRefusal(error));
  }
  const expiresAtMs = Date.parse(response.expiresAt);
  if (!response.token || !Number.isFinite(expiresAtMs))
    return fail(
      new Error('The identity token answer has no token or expiry.'),
      true
    );
  if (startedIn === generation)
    cached = { key, token: response.token, expiresAtMs };
  return response.token;
}

/**
 * The identity token GPTHelp needs to know who is chatting (plan §3.1): the
 * cached one while it has more than a minute left for the same user, store,
 * branch and language; otherwise one request, shared by concurrent callers.
 * Null (the chat stays anonymous, reads still work) outside a store or when
 * the request fails.
 */
export function getAssistantIdentityToken(
  input: IAssistantIdentityInput | null
): Promise<string | null> {
  if (!input) return Promise.resolve(null);
  const key = identityCacheKey(input);
  const now = Date.now();
  if (
    cached &&
    cached.key === key &&
    cached.expiresAtMs - now > IDENTITY_EXPIRY_MARGIN_MS
  )
    return Promise.resolve(cached.token);
  if ((failedUntil.get(key) ?? 0) > now) return Promise.resolve(null);

  const pending = inflight.get(key);
  if (pending) return pending;
  const request = requestToken(key, input).finally(() => {
    if (inflight.get(key) === request) inflight.delete(key);
  });
  inflight.set(key, request);
  return request;
}

/** Forgets every token (another user logged in, or the user logged out). */
export function resetAssistantIdentityToken(): void {
  generation += 1;
  cached = null;
  failedUntil.clear();
  inflight.clear();
}
